import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import { observer } from 'mobx-react';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Table as KSNMTable } from 'ksnm-common-ui/lib/Table2/Table';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { sortBy } from 'lodash-es';
import { v4 } from 'uuid';
import type { LocalDataSource } from 'ksnm-common-ui/lib/Table2/contexts/Table/TableContext.interface';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { confirmDialogStore } from '@stores/confirmDialog';
import { constantStore } from '@stores/constantStore';
import { designDrawerStore } from '@stores/designDrawerStore';
import { filterOperatorsStore } from '@stores/dictionaryStores/filterOperatorsStore';
import { indicatorEditStore } from '@stores/dictionaryStores/indicatorsStore/indicatorEditStore';
import type { DrawerButtonInterface } from '@stores/designDrawerStore';

import { getColumns } from './columns';
import { Form } from './Form';
import { validate } from './validateSchema';

const { TABLE_NAMES } = constantStore;

export const RulesTable = observer(() => {
  const { fields } = useFieldArray('filters');

  const { isEditMode, sourceFields } = indicatorEditStore;
  const { setCustomForm } = designDrawerStore;
  const { open: openConfirmDialog } = confirmDialogStore;
  const { filterOperators } = filterOperatorsStore;

  const [checked, setChecked] = useState<ResponseDataRow[]>([]);

  const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.DICTIONARY_INDICATOR_RULES });

  useEffect(() => {
    update();
  }, [fields.value, update]);

  const {
    input: { value: varSource },
  } = useField('varSource');

  const {
    input: { value: sourceField },
  } = useField('sourceField');

  const localData = useMemo<LocalDataSource>(
    () => ({
      data: sortBy(fields.value ?? [], ['orGroup', 'andGroup']),
      fieldFormat: {
        orGroup: 'string',
        andGroup: 'string',
        fieldName: 'string',
        selOption: 'string',
        fieldValue: 'string',
      },
    }),
    [fields.value],
  );

  const columns = useMemo(() => getColumns(filterOperators, sourceFields), [filterOperators, sourceFields]);

  const onOpenDrawer = useCallback(
    (formData) => {
      const buttons: DrawerButtonInterface[] = [
        {
          key: 'save',
          label: () => <Icon name="save" rightElement="Применить" />,
          submitButton: true,
        },
      ];

      if (formData.id) {
        buttons.unshift({
          editButton: !isEditMode,
          key: 'delete',
          label: () => <Icon name="delete" rightElement="Удалить" fill="#3D5A80" />,
          onClick: () => {
            confirmDialogStore.open({
              onOk: () => {
                const index = fields.value.findIndex((f) => f.id === formData.id);
                setCustomForm(null);
                fields.remove(index);
              },
              text: 'Удалить правило?',
            });
          },
        });
      }

      setCustomForm({
        form: () => <Form filters={fields.value ?? []} sourceField={sourceField} />,
        formData: formData ?? {},
        editMode: !isEditMode,
        validate: validate(fields.value ?? []),
        submit: (values) => {
          const index = (fields.value || [])?.findIndex((f) => f.id === values.id);
          if (index >= 0) {
            fields.remove(index);
          }
          fields.insert(index >= 0 ? index : fields.value?.length, { ...values, id: v4() });
          update();
        },
        buttons,
        title: 'Настройки для формирования',
      });
    },
    [setCustomForm, isEditMode, fields, sourceField, update],
  );

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      const rows = await getLocalRowsByCheckBoxStatus({
        tableRequest: tr,
        localData,
        selectRows: sr,
        keyField: 'id',
      });
      setChecked(rows);
    },
    [localData, setChecked],
  );

  const onRowClick = useCallback(
    (value) => {
      onOpenDrawer({ ...value, orGroup: `${value.orGroup}`, andGroup: `${value.andGroup}` });
    },
    [onOpenDrawer],
  );

  const onCreate = useCallback(() => {
    onOpenDrawer({});
  }, [onOpenDrawer]);

  const onDelete = useCallback(() => {
    openConfirmDialog({
      text: `Удалить выбранные элементы (${checked.length})?`,
      onOk: () => {
        const deleteIndex = checked.map(({ id }) => fields.value.findIndex((f) => f.id === id));

        deleteIndex
          .sort()
          .reverse()
          .forEach((index) => fields.remove(index));
      },
    });
  }, [checked, fields, openConfirmDialog]);

  const [titleWithCount, onTableChange] = useTableTitleWithCount('Настройки для формирования');

  const extendTableButtons = (
    <>
      <IconButton
        onClick={onCreate}
        iconName="add"
        title="Добавить"
        disabled={!isEditMode || varSource === 'rp_package'}
      />
      <IconButton
        onClick={onDelete}
        disabled={checked.length === 0 || !isEditMode || varSource === 'rp_package'}
        iconName="delete"
        title="Удалить"
      />
    </>
  );

  return (
    <KSNMTable
      externalControl={externalControl}
      tableName={TABLE_NAMES.DICTIONARY_INDICATOR_RULES}
      columns={columns}
      tableTitle={titleWithCount}
      resource=""
      keyField="id"
      localDataSource={localData}
      onRowClick={onRowClick}
      onCheckBoxChange={onCheckBoxChange}
      extendTableButtons={extendTableButtons}
      showAggregateRow={false}
      key={key}
      onTableChange={onTableChange}
    />
  );
});
